import { Paper, TextField, useMediaQuery, useTheme } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useEffect, useState, useCallback, useMemo } from "react";
import { IconAndLabelColumnHeader, getGridColDefForArrayValues } from "../../../utils/dataGrid";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import Translate from "../../../utils/Translate";
import { TableAvatar2 } from "../../../utils/dataGrid";
import DotMenu from "../../DotMenu";
import SpeedMenu from "../../SpeedMenu";
import { CustomNoRowsOverlay } from "../../../utils/StyledGridOverlay";
import { dataGridLang } from "../../MemToolBar";
import YesOrNoDialog from "../../YesOrNoDialog";
import UserCrud from "./UserCrud";
import SettingsSystemDaydreamIcon from "@mui/icons-material/SettingsSystemDaydream";

export default function UsersSettings(props) {
  const localizedTextsMap = dataGridLang();
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userCrudProps, setUserCrudProps] = useState(null);
  const [availableReadOnlyProfileTypes, setAvailableReadOnlyProfileTypes] = useState([]);

  const api = useApi();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const speedMenuActions = [];
  speedMenuActions.push({
    icon: <AddIcon />,
    text: Translate.get("AddReadonlyUser"),
    onClick: () => setUserCrudProps({ user: {} }),
  });

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}licenses/eport/users/readonly/self`, false, "GET");
    if (response.isSuccessful) {
      setUsers(response.users);
    }
    setIsLoading(false);
  }, [api]);

  async function deleteUser() {
    setUsers((currentUsers) => currentUsers.filter((t) => t.ePortUserId !== userToDelete));
    await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}licenses/eport/users/readonly?ePortUserId=${userToDelete}`,
      true,
      "DELETE",
    );
    setUserToDelete(null);
    await loadUsers();
  }

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    async function loadAvailableReadOnlyProfiles() {
      const response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}licenses/eport/users/readonly/self/any`,
        false,
        "GET",
      );
      if (response.isSuccessful) {
        setAvailableReadOnlyProfileTypes(response.availableReadOnlyProfileTypes ?? []);
      }
    }

    loadAvailableReadOnlyProfiles();
  }, [api]);

  const getDotMenuActions = useCallback((user) => {
    const menudata = [];
    menudata.push({
      icon: <EditIcon />,
      text: Translate.get("EditUser"),
      onClick: () => setUserCrudProps({ user }),
    });
    menudata.push({
      icon: <DeleteIcon />,
      text: Translate.get("DeleteUser"),
      onClick: () => setUserToDelete(user.ePortUserId),
    });
    return menudata;
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "name",
        flex: 1,
        minWidth: 200,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        renderHeader: () => <IconAndLabelColumnHeader icon={PersonIcon} label={Translate.get("Name")} />,
        renderCell: (params) => (
          <div style={{ marginTop: "1em" }}>
            <TableAvatar2
              image={params.row.profilePictureUrl}
              name={params.row.firstName ?? "" + " " + params.row.lastName}
              email={params.row.ePortAccountId}
            />
          </div>
        ),
      },
      {
        field: "customRole",
        flex: 1,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        sortable: false,
        renderHeader: () => <IconAndLabelColumnHeader icon={AssignmentIndIcon} label={Translate.get("Role")} />,
        renderCell: (params) => params.row.isReadOnly && Translate.get("Readonly"),
      },
      {
        field: "ePortCloudLicenseLineType",
        flex: 1,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        sortable: false,
        renderHeader: () => <IconAndLabelColumnHeader icon={SettingsSystemDaydreamIcon} label={Translate.get("Profile")} />,
        renderCell: (params) => Translate.getEPortLicenseLineType(params.row.ePortCloudLicenseLineType),
      },
      {
        field: "isLocked",
        flex: 0.1,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: "",
        sortable: false,
        align: "right",
        renderCell: (params) => params.row.isLocked && <LockIcon />,
      },
      {
        field: "dotMenu",
        flex: 0.1,
        headerName: "",
        sortable: false,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        align: "right",
        renderCell: (params) => <DotMenu content={getDotMenuActions(params.row)} horizontal />,
      },
    ],
    [getDotMenuActions],
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  // For small screens
  const listViewColDef = {
    field: "listColumn",
    renderCell: (params) => (
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <div style={{ marginTop: "1em" }}>
          <TableAvatar2
            image={params.row.profilePictureUrl}
            name={params.row.firstName ?? "" + " " + params.row.lastName}
            email={params.row.ePortAccountId}
          />
        </div>

        <DotMenu
          content={getDotMenuActions(params.row)}
          horizontal
          style={{ marginLeft: "0.5em", marginTop: "0.2em" }}
        />
      </div>
    ),
  };

  return (
    <>
      {isLoading && <Preloader />}
      {!isLoading && (
        <>
          {userCrudProps && (
            <UserCrud
              onClose={() => setUserCrudProps(null)}
              onSaved={loadUsers}
              availableReadOnlyProfileTypes={availableReadOnlyProfileTypes}
              {...userCrudProps}
            />
          )}
          <SpeedMenu content={speedMenuActions} />
          <Paper>
            <DataGridPremium
              pagination
              pageSizeOptions={[10, 20, 30, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              localeText={localizedTextsMap}
              initialState={{
                sorting: { sortModel: [{ field: "name", sort: "asc" }] },
              }}
              density="comfortable"
              columns={columns}
              rows={users}
              getRowId={(row) => row.ePortUserId}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
              }}
              listView={isSmallScreen}
              listViewColumn={isSmallScreen ? listViewColDef : undefined}
            />
          </Paper>
          <YesOrNoDialog
            open={!!userToDelete}
            text={Translate.get("SureDeleteUserQuestion")}
            onYes={deleteUser}
            onNo={() => setUserToDelete(null)}
          />
        </>
      )}
    </>
  );
}
